// Guided client demo — the 15-minute platform narrative from the BD Handbook, mapped onto the
// Client Workspace tabs. `tab` is the workspace tab to open; `say` is what the presenter says;
// `show` is what to point at on screen. Every step runs on an ILLUSTRATIVE engagement only.
export const DEMO_STEPS = [
  {
    key: "problem",
    tab: "overview",
    eyebrow: "01 · The problem",
    title: "Patient voice is everywhere — and nowhere a team can use it",
    minutes: 1,
    say: [
      "Forums, social media, advocacy groups, PRO surveys, advisory boards, real-world data, HTA submissions — the patient voice is scattered across vendors and buried in reports that nobody reopens.",
      "Teams get anecdotes, not evidence. HTA bodies now ask for patient-experience evidence and reject dossiers that lack it.",
      "Voice2Evidence turns continuous listening into decision-grade, auditable evidence — one living engagement instead of a series of disconnected projects.",
    ],
    show: "The Engagement Brief: asset, indication, lifecycle phase and the decision questions this engagement answers. Point out: this is a live document, not a slide.",
  },
  {
    key: "listening",
    tab: "signals",
    eyebrow: "02 · Listening at scale",
    title: "Every signal is a real patient or caregiver voice, with provenance",
    minutes: 2,
    say: [
      "This is the intake layer. Each row is one signal: a verbatim quote, its source, the evidence stream it came from and the date.",
      "Digital & social listening (L1) runs continuously — not a one-off scrape. New signals keep arriving for as long as the engagement runs.",
      "Nothing is paraphrased away. Teams can always click back to the original voice.",
    ],
    show: "Filter to L1 · Digital & Social Listening. Read one signal aloud. Show the credibility badge — qualitative evidence is graded as qualitative, never dressed up.",
  },
  {
    key: "pentavoice",
    tab: "signals",
    eyebrow: "03 · PentaVoice™ — five evidence streams",
    title: "Five complementary streams, one framework",
    minutes: 2,
    say: [
      "PentaVoice brings five streams together: L1 Digital & Social Listening, L2 PROs & Surveys, L3 Patient Advisory, L4 RWD & RWE, L5 Regulatory & HTA Evidence.",
      "Each stream has its own credibility logic — GRADE for structured evidence, qualitative appraisal for lived experience. Two tracks by design, so there are no category errors.",
      "Fifteen canonical patient-experience themes give every stream the same vocabulary, which is what makes triangulation possible later.",
    ],
    show: "Switch the filter from L1 to L2 · PROs & Surveys: same framework, different credibility grading. Then L3 · Patient Advisory. Emphasise the contrast.",
  },
  {
    key: "concept",
    tab: "concept-map",
    eyebrow: "04 · From signal to concept",
    title: "The Patient Experience Concept Map",
    minutes: 2,
    say: [
      "Signals roll up into concepts, concepts into domains, domains into evidence implications. This is the structured map of what patients actually experience with this asset.",
      "Dense cells are where the evidence is strong; thin cells are where the next research investment should go. The gap analysis is visible in ten seconds.",
    ],
    show: "Point to one dense (green) cell and one thin (amber) cell. Say: 'invest research here next' — this is a decision map, not a word cloud.",
  },
  {
    key: "triangulation",
    tab: "triangulation",
    eyebrow: "05 · Triangulation",
    title: "Where streams converge — and where they disagree",
    minutes: 3,
    say: [
      "A finding only becomes a finding when streams are compared. Convergence raises confidence; divergence is itself an insight — it tells you where the published evidence and lived experience part ways.",
      "Claude drafts the triangulation; the V2E methodology team reviews, edits and publishes. Every claim traces back to its source signals.",
      "This is the moment clients understand what 'provenance' means: published claim → source signals → credibility grades → strategic implication.",
    ],
    show: "Open one finding where three streams converge. Read the interpretation aloud. Click into its source signals to show the audit trail.",
  },
  {
    key: "insight",
    tab: "functions",
    eyebrow: "06 · Insight generation",
    title: "Patient intelligence, re-cast for every team",
    minutes: 2,
    say: [
      "The same triangulated evidence is translated into what each function needs: Medical Affairs, HEOR, Market Access, Regulatory, Commercial, Patient Engagement and Leadership.",
      "One evidence base, many decisions — so the research spend compounds instead of being repeated by every team.",
    ],
    show: "Pick the team your audience belongs to. Show the recommendation and the finding it derives from — nothing is generic.",
  },
  {
    key: "pab",
    tab: "pab",
    eyebrow: "07 · Patient Advisory Brief",
    title: "An advisory board that asks the right questions",
    minutes: 1,
    say: [
      "Most advisory boards ask what the team already believes. This brief is derived from the evidence gaps, so every question tests a specific theme.",
      "It is ready to run a real patient advisory board — and what comes back flows into stream L3 and tightens the triangulation.",
    ],
    show: "Point to one question and the theme it is anchored to.",
  },
  {
    key: "kol",
    tab: "kol",
    eyebrow: "08 · KOL Advisory Brief",
    title: "Positioning · Adoption · Advocacy",
    minutes: 1,
    say: [
      "The KOL brief follows the same logic for clinical leaders: positioning, adoption barriers and advocacy — each grounded in what patients have already said.",
      "Pre-launch KOL boards typically ask the wrong questions; this one asks the right ones because it derives from the evidence.",
    ],
    show: "Show the three-part structure. Mention that briefs export to PDF for the client's own meetings.",
  },
  {
    key: "engagement",
    tab: "overview",
    eyebrow: "09 · How an engagement runs",
    title: "A services engagement, not a software subscription",
    minutes: 1,
    say: [
      "Voice2Evidence is operated by our methodology team. A six-week single-asset pilot answers one priority decision question with fixed scope and fee; acceptance criteria are co-designed with the client.",
      "Then the engagement keeps running: listening continues, evidence compounds, and every function keeps drawing on the same base.",
      "Close with the ask: 'Which decision question would you want us to run this against first?' — then listen.",
    ],
    show: "Return to the Engagement Brief. Leave the decision questions on screen while you ask the closing question.",
  },
];

export const DEMO_TOTAL_MIN = DEMO_STEPS.reduce((n, s) => n + s.minutes, 0);
